import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
} from '@cloudscape-design/components';
import { z } from 'zod';

import {
  approveRegistration,
  createRegistration,
  getRegistration,
  listRegistrations,
  ztrustClientIntentSchema,
  ztrustGroupIntentSchema,
  ztrustRegistrationInputSchema,
  type ZtrustRegistration,
  type ZtrustRegistrationInput,
} from '../../api/ztrust';
import { useAuth } from '../../lib/AuthContext';

interface FormState {
  idempotencyKey: string;
  tenantId: string;
  realm: string;
  templateVersion: string;
  brokerAlias: string;
  brokerDisplayName: string;
  brokerClientId: string;
  brokerSecretReference: string;
  authorizationUrl: string;
  tokenUrl: string;
  userInfoUrl: string;
  issuer: string;
  jwksUrl: string;
  defaultScopes: string;
  loginTheme: string;
  groupsJson: string;
  clientsJson: string;
  isTrustEmail: boolean;
}

interface RegistrationFormModalProps {
  isVisible: boolean;
  isSubmitting: boolean;
  onDismiss: () => void;
  onSubmit: (input: ZtrustRegistrationInput) => Promise<void>;
}

const DEFAULT_GROUPS = JSON.stringify(
  [
    { key: 'application', name: 'application', neverPrimary: false },
    {
      key: 'application_admin',
      name: 'SYSTEM_ADMIN',
      parentKey: 'application',
      precedence: 10,
      neverPrimary: false,
    },
  ],
  null,
  2,
);

const DEFAULT_CLIENTS = JSON.stringify(
  [
    {
      clientId: 'application-web',
      accessType: 'PUBLIC',
      enabled: true,
      standardFlowEnabled: true,
      implicitFlowEnabled: false,
      directAccessGrantsEnabled: false,
      serviceAccountsEnabled: false,
      pkceCodeChallengeMethod: 'S256',
      redirectUris: ['https://replace.example/oauth/callback'],
      webOrigins: ['https://replace.example'],
    },
  ],
  null,
  2,
);

function newFormState(): FormState {
  return {
    idempotencyKey: crypto.randomUUID(),
    tenantId: '',
    realm: '',
    templateVersion: '1.0.0',
    brokerAlias: '',
    brokerDisplayName: '',
    brokerClientId: '',
    brokerSecretReference: '',
    authorizationUrl: '',
    tokenUrl: '',
    userInfoUrl: '',
    issuer: '',
    jwksUrl: '',
    defaultScopes: 'openid email profile',
    loginTheme: '',
    groupsJson: DEFAULT_GROUPS,
    clientsJson: DEFAULT_CLIENTS,
    isTrustEmail: true,
  };
}

export function ZtrustRegistrationView(): ReactElement {
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<ZtrustRegistration[]>([]);
  const [selected, setSelected] = useState<ZtrustRegistration | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isApprovalVisible, setIsApprovalVisible] = useState(false);
  const [isApprovalConfirmed, setIsApprovalConfirmed] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  const load = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(undefined);
    try {
      setRegistrations(await listRegistrations());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load registrations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const isApprover = user?.groups.some((group) => ['Admin', 'ABPAdmin'].includes(group)) ?? false;
  const canApprove =
    selected?.status === 'pending-approval' &&
    isApprover &&
    selected.createdBy !== user?.id;

  async function selectRegistration(item: ZtrustRegistration | undefined): Promise<void> {
    setSelected(item);
    if (!item) return;
    try {
      setSelected(await getRegistration(item.registrationId));
    } catch (selectionError) {
      setError(selectionError instanceof Error ? selectionError.message : 'Unable to load registration');
    }
  }

  async function submitRegistration(input: ZtrustRegistrationInput): Promise<void> {
    setIsCreating(true);
    setError(undefined);
    try {
      const result = await createRegistration(input);
      setIsFormVisible(false);
      setSelected(result.registration);
      setNotice(
        result.idempotentReplay
          ? 'The existing idempotent registration was returned.'
          : 'Registration recorded and awaiting independent approval.',
      );
      await load();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to create registration');
    } finally {
      setIsCreating(false);
    }
  }

  async function approveSelected(): Promise<void> {
    if (!selected || !isApprovalConfirmed) return;
    setIsApproving(true);
    setError(undefined);
    try {
      const result = await approveRegistration(selected);
      setSelected(result.registration);
      setIsApprovalVisible(false);
      setIsApprovalConfirmed(false);
      setNotice('Registration approved. Provisioning was not triggered; Terraform remains the writer.');
      await load();
    } catch (approvalError) {
      setError(approvalError instanceof Error ? approvalError.message : 'Unable to approve registration');
    } finally {
      setIsApproving(false);
    }
  }

  return (
    <SpaceBetween size="m">
      <RegistrationAlerts error={error} notice={notice} />
      <RegistrationTable
        registrations={registrations}
        selected={selected}
        isLoading={isLoading}
        canApprove={canApprove}
        onRefresh={load}
        onCreate={() => setIsFormVisible(true)}
        onSelect={selectRegistration}
        onApprove={() => setIsApprovalVisible(true)}
      />
      {selected && <RegistrationDetail registration={selected} />}
      <RegistrationFormModal
        isVisible={isFormVisible}
        isSubmitting={isCreating}
        onDismiss={() => setIsFormVisible(false)}
        onSubmit={submitRegistration}
      />
      <ApprovalModal
        registration={selected}
        isVisible={isApprovalVisible}
        isConfirmed={isApprovalConfirmed}
        isSubmitting={isApproving}
        onConfirm={setIsApprovalConfirmed}
        onDismiss={() => setIsApprovalVisible(false)}
        onApprove={approveSelected}
      />
    </SpaceBetween>
  );
}

function RegistrationAlerts({
  error,
  notice,
}: {
  error: string | undefined;
  notice: string | undefined;
}): ReactElement {
  return (
    <SpaceBetween size="s">
      <Alert type="info" header="Approval records declarative intent only">
        Approved records are durable inputs for an approved Terraform runner. Peregrine does not call
        Keycloak and no provisioning worker is enabled until a reviewed pipeline trigger contract exists.
      </Alert>
      {error ? <Alert type="error">{error}</Alert> : null}
      {notice ? <Alert type="success">{notice}</Alert> : null}
    </SpaceBetween>
  );
}

interface RegistrationTableProps {
  registrations: ZtrustRegistration[];
  selected: ZtrustRegistration | undefined;
  isLoading: boolean;
  canApprove: boolean;
  onRefresh: () => Promise<void>;
  onCreate: () => void;
  onSelect: (registration: ZtrustRegistration | undefined) => Promise<void>;
  onApprove: () => void;
}

function RegistrationTable(props: RegistrationTableProps): ReactElement {
  return (
    <Table
      items={props.registrations}
      loading={props.isLoading}
      selectionType="single"
      selectedItems={props.selected ? [props.selected] : []}
      onSelectionChange={({ detail }) => void props.onSelect(detail.selectedItems[0])}
      trackBy="registrationId"
      empty={<Box textAlign="center">No ZTrust registrations.</Box>}
      header={
        <Header
          variant="h2"
          counter={`(${props.registrations.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" onClick={() => void props.onRefresh()} loading={props.isLoading}>
                Refresh
              </Button>
              <Button disabled={!props.canApprove} onClick={props.onApprove}>
                Approve reviewed revision
              </Button>
              <Button variant="primary" onClick={props.onCreate}>Register tenant realm</Button>
            </SpaceBetween>
          }
        >
          Registration and approval
        </Header>
      }
      columnDefinitions={[
        { id: 'tenant', header: 'Tenant', cell: (item) => item.tenantId },
        { id: 'realm', header: 'Realm', cell: (item) => item.realm },
        {
          id: 'status',
          header: 'Status',
          cell: (item) => (
            <StatusIndicator type={item.status === 'approved' ? 'success' : 'pending'}>
              {item.status}
            </StatusIndicator>
          ),
        },
        { id: 'revision', header: 'Revision', cell: (item) => item.revision },
        {
          id: 'hash',
          header: 'Desired-state hash',
          cell: (item) => <code>{item.desiredStateHash.slice(0, 12)}</code>,
        },
        { id: 'created', header: 'Created', cell: (item) => new Date(item.createdAt).toLocaleString() },
      ]}
    />
  );
}

function RegistrationDetail({ registration }: { registration: ZtrustRegistration }): ReactElement {
  return (
    <Container header={<Header variant="h3">Selected desired state</Header>}>
      <ColumnLayout columns={3} variant="text-grid">
        <div><Box variant="awsui-key-label">Registration</Box><code>{registration.registrationId}</code></div>
        <div><Box variant="awsui-key-label">Revision</Box><Box>{registration.revision}</Box></div>
        <div><Box variant="awsui-key-label">Template</Box><Box>{registration.templateVersion}</Box></div>
        <div><Box variant="awsui-key-label">Broker</Box><Box>{registration.desiredState.broker.alias}</Box></div>
        <div><Box variant="awsui-key-label">Groups</Box><Box>{registration.desiredState.groups.length}</Box></div>
        <div><Box variant="awsui-key-label">Clients</Box><Box>{registration.desiredState.clients.length}</Box></div>
      </ColumnLayout>
      <Box padding={{ top: 's' }}>
        <Box variant="awsui-key-label">Full SHA-256 desired-state hash</Box>
        <code>{registration.desiredStateHash}</code>
      </Box>
    </Container>
  );
}

function RegistrationFormModal(props: RegistrationFormModalProps): ReactElement {
  const [form, setForm] = useState<FormState>(newFormState);
  const [error, setError] = useState<string | undefined>();
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    if (props.isVisible) {
      setForm(newFormState());
      setError(undefined);
      setIsConfirmed(false);
    }
  }, [props.isVisible]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(): Promise<void> {
    const parsed = buildRegistrationInput(form);
    if (!parsed.success) {
      setError(parsed.error);
      return;
    }
    setError(undefined);
    await props.onSubmit(parsed.data);
  }

  return (
    <Modal
      visible={props.isVisible}
      onDismiss={props.onDismiss}
      header="Register ZTrust tenant realm"
      size="max"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={props.onDismiss}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!isConfirmed}
              loading={props.isSubmitting}
              onClick={() => void submit()}
            >
              Record pending approval
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error ? <Alert type="error">{error}</Alert> : null}
        <Alert type="warning">
          Enter secret ARNs only. Secret values, passwords, and client-secret material are rejected by the API.
        </Alert>
        <TenantFields form={form} update={update} />
        <BrokerFields form={form} update={update} />
        <IntentJsonFields form={form} update={update} />
        <Alert type="info" header="Enforced hardening">
          SSL required for all requests; registration and local password reset disabled; e-mail verification
          and brute-force protection enabled. Direct grants and implicit flow are rejected.
        </Alert>
        <Checkbox checked={isConfirmed} onChange={({ detail }) => setIsConfirmed(detail.checked)}>
          I reviewed this desired state and confirm it contains references only, not secret values.
        </Checkbox>
      </SpaceBetween>
    </Modal>
  );
}

type UpdateForm = <K extends keyof FormState>(key: K, value: FormState[K]) => void;

function TenantFields({ form, update }: { form: FormState; update: UpdateForm }): ReactElement {
  return (
    <ColumnLayout columns={3}>
      <FormField label="Tenant ID"><Input value={form.tenantId} onChange={({ detail }) => update('tenantId', detail.value)} /></FormField>
      <FormField label="Realm"><Input value={form.realm} onChange={({ detail }) => update('realm', detail.value)} /></FormField>
      <FormField label="Template version"><Input value={form.templateVersion} onChange={({ detail }) => update('templateVersion', detail.value)} /></FormField>
      <FormField label="Login theme"><Input value={form.loginTheme} onChange={({ detail }) => update('loginTheme', detail.value)} /></FormField>
    </ColumnLayout>
  );
}

function BrokerFields({ form, update }: { form: FormState; update: UpdateForm }): ReactElement {
  return (
    <SpaceBetween size="s">
      <Header variant="h3">OIDC broker</Header>
      <ColumnLayout columns={3}>
        <FormField label="Alias"><Input value={form.brokerAlias} onChange={({ detail }) => update('brokerAlias', detail.value)} /></FormField>
        <FormField label="Display name"><Input value={form.brokerDisplayName} onChange={({ detail }) => update('brokerDisplayName', detail.value)} /></FormField>
        <FormField label="Client ID"><Input value={form.brokerClientId} onChange={({ detail }) => update('brokerClientId', detail.value)} /></FormField>
      </ColumnLayout>
      <FormField label="Broker secret ARN" constraintText="Reference only; the secret value never enters Peregrine">
        <Input value={form.brokerSecretReference} onChange={({ detail }) => update('brokerSecretReference', detail.value)} />
      </FormField>
      <ColumnLayout columns={2}>
        <FormField label="Authorization URL"><Input value={form.authorizationUrl} onChange={({ detail }) => update('authorizationUrl', detail.value)} /></FormField>
        <FormField label="Token URL"><Input value={form.tokenUrl} onChange={({ detail }) => update('tokenUrl', detail.value)} /></FormField>
        <FormField label="User info URL"><Input value={form.userInfoUrl} onChange={({ detail }) => update('userInfoUrl', detail.value)} /></FormField>
        <FormField label="Issuer"><Input value={form.issuer} onChange={({ detail }) => update('issuer', detail.value)} /></FormField>
        <FormField label="JWKS URL"><Input value={form.jwksUrl} onChange={({ detail }) => update('jwksUrl', detail.value)} /></FormField>
        <FormField label="Default scopes"><Input value={form.defaultScopes} onChange={({ detail }) => update('defaultScopes', detail.value)} /></FormField>
      </ColumnLayout>
      <Checkbox checked={form.isTrustEmail} onChange={({ detail }) => update('isTrustEmail', detail.checked)}>
        Trust broker-verified e-mail
      </Checkbox>
    </SpaceBetween>
  );
}

function IntentJsonFields({ form, update }: { form: FormState; update: UpdateForm }): ReactElement {
  return (
    <ColumnLayout columns={2}>
      <FormField label="Groups and explicit precedence" description="JSON array; parentKey supports one nesting level">
        <Textarea value={form.groupsJson} rows={14} onChange={({ detail }) => update('groupsJson', detail.value)} />
      </FormField>
      <FormField label="Clients" description="JSON array; public authorization-code clients require PKCE S256">
        <Textarea value={form.clientsJson} rows={14} onChange={({ detail }) => update('clientsJson', detail.value)} />
      </FormField>
    </ColumnLayout>
  );
}

function buildRegistrationInput(form: FormState):
  | { success: true; data: ZtrustRegistrationInput }
  | { success: false; error: string } {
  const groups = parseArray(form.groupsJson, ztrustGroupIntentSchema, 'groups');
  if (!groups.success) return groups;
  const clients = parseArray(form.clientsJson, ztrustClientIntentSchema, 'clients');
  if (!clients.success) return clients;

  const candidate: unknown = {
    idempotencyKey: form.idempotencyKey,
    tenantId: form.tenantId.trim(),
    realm: form.realm.trim(),
    templateVersion: form.templateVersion.trim(),
    broker: {
      alias: form.brokerAlias.trim(),
      providerId: 'oidc',
      displayName: form.brokerDisplayName.trim(),
      authorizationUrl: form.authorizationUrl.trim(),
      tokenUrl: form.tokenUrl.trim(),
      userInfoUrl: form.userInfoUrl.trim(),
      issuer: form.issuer.trim(),
      jwksUrl: form.jwksUrl.trim(),
      clientId: form.brokerClientId.trim(),
      secretReference: form.brokerSecretReference.trim(),
      defaultScopes: form.defaultScopes.split(/\s+/).filter(Boolean),
      trustEmail: form.isTrustEmail,
    },
    groups: groups.data,
    clients: clients.data,
    hardening: {
      registrationAllowed: false,
      resetPasswordAllowed: false,
      verifyEmail: true,
      bruteForceProtected: true,
      sslRequired: 'all',
      loginTheme: form.loginTheme.trim() || null,
    },
  };
  const parsed = ztrustRegistrationInputSchema.safeParse(candidate);
  if (!parsed.success) {
    return { success: false, error: parsed.error.issues[0]?.message ?? 'Desired state is invalid' };
  }
  return { success: true, data: parsed.data };
}

function parseArray<T>(
  value: string,
  itemSchema: z.ZodType<T>,
  label: string,
): { success: true; data: T[] } | { success: false; error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return { success: false, error: `${label} must be valid JSON` };
  }
  const parsed = z.array(itemSchema).safeParse(raw);
  if (!parsed.success) {
    return { success: false, error: `${label}: ${parsed.error.issues[0]?.message ?? 'invalid array'}` };
  }
  return { success: true, data: parsed.data };
}

interface ApprovalModalProps {
  registration: ZtrustRegistration | undefined;
  isVisible: boolean;
  isConfirmed: boolean;
  isSubmitting: boolean;
  onConfirm: (confirmed: boolean) => void;
  onDismiss: () => void;
  onApprove: () => Promise<void>;
}

function ApprovalModal(props: ApprovalModalProps): ReactElement {
  return (
    <Modal
      visible={props.isVisible}
      onDismiss={props.onDismiss}
      header="Approve exact ZTrust revision"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={props.onDismiss}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!props.registration || !props.isConfirmed}
              loading={props.isSubmitting}
              onClick={() => void props.onApprove()}
            >
              Approve revision
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">
          This conditionally approves only the displayed revision and hash. It does not provision ZTrust.
        </Alert>
        <Box variant="awsui-key-label">Revision</Box>
        <Box>{props.registration?.revision ?? '—'}</Box>
        <Box variant="awsui-key-label">Desired-state hash</Box>
        <code>{props.registration?.desiredStateHash ?? '—'}</code>
        <Checkbox checked={props.isConfirmed} onChange={({ detail }) => props.onConfirm(detail.checked)}>
          I independently reviewed this exact revision and SHA-256 hash.
        </Checkbox>
      </SpaceBetween>
    </Modal>
  );
}
